import { DateTimeCustomOption, DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC, IDB } from '@/core/db'
import cookies from '@/lib/cookies'
import { generateRandomId } from '@/lib/random-id'

import { globalVariableClient } from '../global-variables'
import { parseVariableDateTime } from './datetime'
import { parseProfileData } from './profile-data'
import { parseSingleCellValue } from './single-cell'

export function parseVariableSelect(
  item?: DefaultVariableResult,
  dbClient?: IDB,
  userEmail?: string,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  tableId?: string,
  rowId?: string,
  withColumnFormat = true
) {
  // if its variable select
  if (!(typeof item === 'object' && item !== null && 'flag' in item) || !dbClient) {
    return []
  }

  const settings = item as DefaultVariableResult

  switch (settings.flag) {
    case VariableSelectFeatureFlag.ColumnData: {
      const table = dbClient.tablesMap.get(settings.tbl!)
      if (!table) return []

      const rowIdx = rowId ? table.GetRowIndexByRowId(rowId, lang, user) : -1

      const columnDataValue = parseSingleCellValue(
        {
          tbl: settings.tbl,
          col: settings.col,
          row: String(rowIdx),
        },
        dbClient,
        lang,
        user,
        withColumnFormat
      )
      if (!columnDataValue) return []
      return [columnDataValue]
    }

    case VariableSelectFeatureFlag.CurrentListItem: {
      const table = dbClient.tablesMap.get(tableId!)
      if (!table) return []

      const rowIdx = rowId ? table.GetRowIndexByRowId(rowId, lang, user) : -1

      const currentListValue = parseSingleCellValue(
        {
          tbl: tableId,
          row: String(rowIdx),
          col: settings.col,
        },
        dbClient,
        lang,
        user,
        withColumnFormat
      )
      if (!currentListValue) return []
      return [currentListValue]
    }

    case VariableSelectFeatureFlag.CurrentRow: {
      const table = dbClient.tablesMap.get(tableId!)
      if (!table) return []

      const rowIdx = rowId ? table.GetRowIndexByRowId(rowId, lang, user) : -1

      const currentRowValue = parseSingleCellValue(
        {
          tbl: tableId,
          row: String(rowIdx),
          col: settings.col,
        },
        dbClient,
        lang,
        user,
        withColumnFormat
      )
      if (!currentRowValue) return []
      return [currentRowValue]
    }

    case VariableSelectFeatureFlag.Column: {
      const table = dbClient.tablesMap.get(tableId!)
      if (!table) return []

      const rowIdx = rowId ? table.GetRowIndexByRowId(rowId, lang, user) : -1

      const columnValue = parseSingleCellValue(
        {
          tbl: tableId,
          row: String(rowIdx),
          col: settings.col,
        },
        dbClient,
        lang,
        user,
        withColumnFormat
      )
      if (!columnValue) return []
      return [columnValue]
    }

    case VariableSelectFeatureFlag.SingleCell: {
      const singleCellValue = parseSingleCellValue(
        {
          ...settings,
          row: String(Number(settings.row) - 1),
        },
        dbClient,
        lang,
        user,
        withColumnFormat
      )
      if (!singleCellValue) return []
      return [singleCellValue]
    }

    case VariableSelectFeatureFlag.ProfileData: {
      const profileDataValue = parseProfileData(settings.col, dbClient, userEmail)
      if (!profileDataValue) return []
      return [profileDataValue]
    }

    case VariableSelectFeatureFlag.SessionID: {
      const sessionIDValue = cookies.getAppSessionId(window.appID)
      if (!sessionIDValue) return []
      return [sessionIDValue]
    }

    case VariableSelectFeatureFlag.UniqueID: {
      const uniqueIDValue = generateRandomId()
      if (!uniqueIDValue) return []
      return [uniqueIDValue]
    }

    case VariableSelectFeatureFlag.Icon:
      return [settings.value]

    case VariableSelectFeatureFlag.Space: {
      const spaceValue = ' '
      return [spaceValue]
    }

    case VariableSelectFeatureFlag.DateTime: {
      const dateTimeValue = parseVariableDateTime(settings.variant, false)
      return [dateTimeValue]
    }

    case VariableSelectFeatureFlag.CurrentDateTime: {
      const currentDateTimeValue = parseVariableDateTime(DateTimeCustomOption.Now)
      return [currentDateTimeValue]
    }

    case VariableSelectFeatureFlag.Image: {
      const imageValue = settings.value
      return [imageValue]
    }

    // TODO
    case VariableSelectFeatureFlag.SearchBox:
    case VariableSelectFeatureFlag.Avatar:
      return ['']
    case VariableSelectFeatureFlag.RichText:
    case VariableSelectFeatureFlag.Menu:
    case VariableSelectFeatureFlag.CustomText:
    case VariableSelectFeatureFlag.ActionOutput: // TODO: handle action output, get the value here
    case VariableSelectFeatureFlag.File: {
      let value = item as any
      return [value.value ?? '']
    }
    case VariableSelectFeatureFlag.GlobalVariable: {
      const varName = settings.value

      const globalVariableValue = globalVariableClient?.getValue(varName)
      if (!globalVariableValue) return []

      return [globalVariableValue]
    }
    default:
      return []
  }
}
